import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import React, { useState } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native';
// NOTE: uuid is no longer needed for keys; keep it only if used elsewhere
// import uuid from 'react-native-uuid';

import Avatar, { AvatarProps } from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { Box, Text } from '@components/Restyle/index';
import theme from '@themes/theme';

export interface MemberListSummaryProps {
  avatars: AvatarProps[];
  onPress?: () => void;
  disabled?: boolean;
  maxAvatars?: number;
  isReadBy?: boolean;
  isSmall?: boolean;
  marginLeft?: number;
  showTooltip?: boolean;
}

export const MemberListSummaryV3 = ({
  avatars,
  onPress,
  disabled,
  maxAvatars = 3,
  isReadBy = false,
  isSmall = false,
  marginLeft = -14,
  showTooltip = false,
}: MemberListSummaryProps) => {
  const counter = avatars.length - maxAvatars;
  const { width } = Dimensions.get('window');
  const [isHovered, setIsHovered] = useState<string | undefined>(undefined);

  // Compute base avatar container style depending on layout and size
  const getBaseStyle = () => {
    if (width < 1025) {
      return styles.responsiveAvatarContainer;
    }
    return isSmall
      ? styles.smallAvatarContainer
      : { ...styles.avatarContainer, marginLeft: marginLeft };
  };

  const containerStyle = [
    getBaseStyle(),
    isReadBy && styles.readByAvatarContainer,
  ];

  // Helper to build a stable key for each avatar item
  const getAvatarKey = (avatar: AvatarProps, index: number, prefix = '') => {
    // Prefer explicit id from props, then nested avatar id, then label, then index
    return (
      (avatar.id as string | undefined) ||
      (avatar.avatar?.id as string | undefined) ||
      (avatar.label as string | undefined) ||
      `${prefix}avatar-${index}`
    );
  };

  const counterBox = () => {
    if (counter <= 0) return null;

    const extraAvatars = avatars.filter((_, index) => index >= maxAvatars);

    return (
      <TooltipWebV3
        marginOffset={0}
        disableHoverListener={!showTooltip}
        disableFocusListener={!showTooltip}
        disableTouchListener={!showTooltip}
        title={
          <Box>
            {extraAvatars.map((avatar, index) => {
              const key =
                avatar.accessibilityLabel === 'TaskInvitation'
                  ? avatar.label
                  : getAvatarKey(avatar, index, 'extra-');
              return (
                <Box
                  key={key}
                  mt={index !== 0 ? 'xs' : 'none'}
                  flexDirection='row'
                  alignItems='center'>
                  {avatar.accessibilityLabel === 'TaskInvitation' ? (
                    <EmailAvatar width={16} icon='User' />
                  ) : (
                    <Avatar {...avatar} width={16} size='xs' height={16} />
                  )}

                  <Text
                    ml='xxs'
                    accessibilityLabel='User Name'
                    variant='labelSmall'
                    color='white'
                    style={{ letterSpacing: 0.02 }}
                    maxWidth={150}
                    ellipsizeMode='tail'
                    numberOfLines={1}>
                    {avatar.label}
                  </Text>
                </Box>
              );
            })}
          </Box>
        }
        placement='bottom-end'>
        <div>
          <Box
            style={[
              containerStyle,
              {
                borderColor: theme.colors.grey01,
                backgroundColor: theme.colors.grey01,
              },
            ]}>
            <Text variant='webBody' color='textSecondary'>
              +{counter.toString()}
            </Text>
          </Box>
        </div>
      </TooltipWebV3>
    );
  };

  return (
    <TouchableOpacity onPress={onPress} disabled={disabled}>
      <Box flexDirection='row'>
        {avatars
          .filter(
            (_, index) => index < (counter === 1 ? maxAvatars + 1 : maxAvatars)
          )
          .map((avatar, index) => {
            // Skip invalid index guard (kept from your original code)
            if (index < 0) return null;

            const key =
              avatar.accessibilityLabel === 'TaskInvitation'
                ? avatar.label
                : getAvatarKey(avatar, index, 'extra-');

            return (
              <TooltipWebV3
                key={key}
                marginOffset={0}
                disableHoverListener={!showTooltip}
                disableFocusListener={!showTooltip}
                disableTouchListener={!showTooltip}
                title={
                  <Box flexDirection='row' alignItems='center'>
                    {avatar.accessibilityLabel === 'TaskInvitation' ? (
                      <EmailAvatar width={16} icon='User' />
                    ) : (
                      <Avatar {...avatar} width={16} size='xs' height={16} />
                    )}
                    <Text
                      ml='xxs'
                      accessibilityLabel='User Name'
                      variant='labelSmall'
                      color='white'
                      style={{ letterSpacing: 0.02 }}
                      maxWidth={150}
                      ellipsizeMode='tail'
                      numberOfLines={1}>
                      {avatar.label}
                    </Text>
                  </Box>
                }
                placement='bottom-end'>
                <div>
                  <Box
                    style={containerStyle}
                    onMouseEnter={() => setIsHovered(avatar.id as string)}
                    onMouseLeave={() => setIsHovered(undefined)}>
                    {avatar.accessibilityLabel === 'TaskInvitation' ? (
                      <EmailAvatar width={32} icon='User' />
                    ) : (
                      <Avatar {...avatar} isSmall={isSmall} />
                    )}
                    {isHovered === avatar.id && (
                      <Box
                        position='absolute'
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        style={{ backgroundColor: '#FFFFFF80' }}
                        borderRadius='m'
                        pointerEvents='none'
                      />
                    )}
                  </Box>
                </div>
              </TooltipWebV3>
            );
          })}

        {avatars.length > maxAvatars + 1 && counterBox()}
      </Box>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  avatarContainer: {
    width: Platform.OS === 'web' ? 32 : 24,
    height: Platform.OS === 'web' ? 32 : 24,
    borderRadius: Platform.OS === 'web' ? 16 : 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'grey01',
    borderWidth: 1,
    borderColor: 'white',
  },
  smallAvatarContainer: {
    marginLeft: -14,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'black',
  },
  responsiveAvatarContainer: {
    marginLeft: -10,
    width: 24,
    height: 24,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'black',
    borderWidth: 1,
    borderColor: 'white',
  },
  readByAvatarContainer: {
    marginLeft: -9,
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'black',
    borderWidth: 1,
    borderColor: 'white',
  },
});
